import { isAxiosError } from 'axios'
import {
  type Artifact,
  ArtifactSchema,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'

export const ARTIFACTS_API_PATH = '/api/v2/artifacts'

/** The slice of the authed axios client the artifact endpoints use. */
export interface ArtifactApiClient {
  get: (url: string) => Promise<{ data: unknown }>
  post: (url: string, body: FormData) => Promise<{ data: unknown }>
}

export function artifactUrl(
  powerBackendUrl: string,
  artifactId: string,
  siteId?: string
): string {
  const base = `${powerBackendUrl}${ARTIFACTS_API_PATH}/${encodeURIComponent(artifactId)}`
  if (!siteId) return base
  return `${base}?${new URLSearchParams({ site_id: siteId }).toString()}`
}

export function artifactDownloadsUrl(
  powerBackendUrl: string,
  artifactId: string
): string {
  return `${artifactUrl(powerBackendUrl, artifactId)}/downloads`
}

export function parseArtifact(data: unknown): Artifact {
  return ArtifactSchema.parse(data)
}

export async function fetchArtifact(
  axios: ArtifactApiClient,
  url: string
): Promise<Artifact> {
  const response = await axios.get(url)
  return parseArtifact(response.data)
}

export async function postArtifactDownload(
  axios: ArtifactApiClient,
  url: string,
  {
    siteId,
    revision,
    file,
    filename,
  }: {
    siteId: string
    revision: number
    file: Blob
    filename: string
  }
): Promise<Artifact> {
  const form = new FormData()
  form.append('site_id', siteId)
  form.append('revision', String(revision))
  form.append('file', file, filename)
  const response = await axios.post(url, form)
  return parseArtifact(response.data)
}

/** The downloads endpoint answers 409 when the rendered revision is no longer the artifact's current one. */
export function isArtifactRevisionConflict(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 409
}
